/**
 * Helpdesk — the service desk behind the "Help desk" menu item (ADR 0028).
 *
 * ONE ROUTER, THREE AUDIENCES, SEPARATED PER TICKET AND NOT BY PATH. A raiser (`ticket:read`) sees
 * their own tickets; an agent (`ticket:manage`, implicit from helpdesk-team membership) sees the
 * tickets of their teams' categories; a Helpdesk Manager (`ticket.all:manage`) sees every one. A
 * ticket the caller may not see is a 404, never a 403 — its existence is not the caller's business.
 * A confidential category is visible to its own team and the managers only, with no exceptions.
 *
 * INTERNAL NOTES ARE FILTERED SERVER-SIDE. A raiser's view of a ticket omits every internal
 * comment, internal attachment and internal activity entry — whatever other role the raiser
 * holds. The rows are not sent and flagged; they are not sent.
 *
 * SLA DUE INSTANTS ARE STORED, and `slaClock` says whether they were measured in the location's
 * business hours or in calendar hours (ADR 0028 §6). Screens show which.
 *
 * TICKET READS ARE HIDDEN FROM THE AI COPILOT (`ai: 'hidden'`): a ticket is free text an employee
 * wrote, often about something confidential, and it does not go into a prompt.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  HELPDESK_ASSIGNMENT_MODES,
  HELPDESK_EVENT_KINDS,
  HELPDESK_PRIORITIES,
  HELPDESK_SLA_CLOCKS,
  HELPDESK_TICKET_STATUSES,
  HELPDESK_VISIBILITIES,
} from '../enums.js';
import { errorBodySchema, instantSchema, uuidSchema } from '../schemas/common.js';
import { routeMeta } from './meta.js';

const c = initContract();

export const helpdeskTicketStatusSchema = z.enum(HELPDESK_TICKET_STATUSES);
export const helpdeskPrioritySchema = z.enum(HELPDESK_PRIORITIES);
export const helpdeskVisibilitySchema = z.enum(HELPDESK_VISIBILITIES);
export const helpdeskAssignmentModeSchema = z.enum(HELPDESK_ASSIGNMENT_MODES);
export const helpdeskSlaClockSchema = z.enum(HELPDESK_SLA_CLOCKS);
export const helpdeskEventKindSchema = z.enum(HELPDESK_EVENT_KINDS);

/** `HH:MM`, 24-hour. */
const clockTimeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use HH:MM');
const personRefSchema = z.object({ personId: uuidSchema, name: z.string() });
export type HelpdeskPersonRef = z.infer<typeof personRefSchema>;

const ticketParams = z.object({ ticketId: uuidSchema });

/** Who may see a ticket, read by list and detail alike. See the header. */
export const helpdeskTicketScopeSchema = z.enum(['mine', 'assigned', 'queue', 'all']);
export type HelpdeskTicketScope = z.infer<typeof helpdeskTicketScopeSchema>;

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

export const helpdeskCategorySchema = z.object({
  id: uuidSchema,
  name: z.string(),
  description: z.string().nullable(),
  /** Only the owning team and Helpdesk Managers see its tickets. */
  isConfidential: z.boolean(),
  teamId: uuidSchema.nullable(),
  teamName: z.string().nullable(),
  sortOrder: z.number().int(),
  isActive: z.boolean(),
});
export type HelpdeskCategory = z.infer<typeof helpdeskCategorySchema>;

const categoryName = z.string().trim().min(2).max(80);
const optionalText = (max: number) => z.string().trim().max(max).nullable().optional();

export const createHelpdeskCategoryBodySchema = z.object({
  name: categoryName,
  description: optionalText(500),
  isConfidential: z.boolean().default(false),
  teamId: uuidSchema.nullable().optional(),
  sortOrder: z.number().int().min(0).max(1000).optional(),
});
export const updateHelpdeskCategoryBodySchema = z.object({
  name: categoryName.optional(),
  description: optionalText(500),
  isConfidential: z.boolean().optional(),
  teamId: uuidSchema.nullable().optional(),
  sortOrder: z.number().int().min(0).max(1000).optional(),
  /** Deactivating stops new tickets; existing ones keep their category. Nothing is deleted. */
  isActive: z.boolean().optional(),
});

export const helpdeskTeamSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  description: z.string().nullable(),
  assignmentMode: helpdeskAssignmentModeSchema,
  /** Notified of SLA breaches along with the Helpdesk Managers. Must be a member. */
  lead: personRefSchema.nullable(),
  members: z.array(personRefSchema),
  categoryIds: z.array(uuidSchema),
  isActive: z.boolean(),
});
export type HelpdeskTeam = z.infer<typeof helpdeskTeamSchema>;

export const createHelpdeskTeamBodySchema = z.object({
  name: z.string().trim().min(2).max(80),
  description: optionalText(500),
  assignmentMode: helpdeskAssignmentModeSchema.default('round_robin'),
  memberPersonIds: z.array(uuidSchema).max(200).default([]),
  leadPersonId: uuidSchema.nullable().optional(),
});
export const updateHelpdeskTeamBodySchema = z.object({
  name: z.string().trim().min(2).max(80).optional(),
  description: optionalText(500),
  assignmentMode: helpdeskAssignmentModeSchema.optional(),
  /** Replaces the member set. Adding or removing a member changes their implicit agent role. */
  memberPersonIds: z.array(uuidSchema).max(200).optional(),
  leadPersonId: uuidSchema.nullable().optional(),
  isActive: z.boolean().optional(),
});

export const helpdeskSlaTargetSchema = z.object({
  categoryId: uuidSchema,
  priority: helpdeskPrioritySchema,
  /** Working minutes (or calendar minutes, per the ticket's clock) to the first agent reply. */
  firstResponseMinutes: z.number().int().min(1).max(525_600),
  resolutionMinutes: z.number().int().min(1).max(525_600),
});
export type HelpdeskSlaTarget = z.infer<typeof helpdeskSlaTargetSchema>;

export const putHelpdeskSlaTargetsBodySchema = z.object({
  /** Replaces the whole matrix. A category × priority left out has no SLA. */
  targets: z.array(helpdeskSlaTargetSchema).max(2000),
});

export const helpdeskSettingsSchema = z.object({
  /** `business` measures SLAs in each ticket location's working hours; `calendar` is 24×7. */
  slaClock: helpdeskSlaClockSchema,
  businessDayStart: clockTimeSchema,
  businessDayEnd: clockTimeSchema,
  /** Days after resolution the raiser may reopen; also when a resolved ticket auto-closes. */
  reopenWindowDays: z.number().int().min(0).max(90),
});
export type HelpdeskSettings = z.infer<typeof helpdeskSettingsSchema>;

/** What the caller is in the helpdesk — so a screen offers the tabs that will work. */
export const helpdeskMeSchema = z.object({
  canRaise: z.boolean(),
  isAgent: z.boolean(),
  teamIds: z.array(uuidSchema),
  canSeeAllTickets: z.boolean(),
  canConfigure: z.boolean(),
  canViewReports: z.boolean(),
  availableScopes: z.array(helpdeskTicketScopeSchema),
});
export type HelpdeskMe = z.infer<typeof helpdeskMeSchema>;

// ---------------------------------------------------------------------------
// Tickets
// ---------------------------------------------------------------------------

export const helpdeskSlaStateSchema = z.object({
  dueAt: instantSchema.nullable(),
  /** When it was met (first reply / resolution), or null while outstanding. */
  metAt: instantSchema.nullable(),
  /** True once breached — recorded by the sweep, or evident because the due instant passed. */
  isBreached: z.boolean(),
  /** Resolution only: the clock is stopped while waiting on the employee. */
  isPaused: z.boolean(),
});

export const helpdeskTicketRowSchema = z.object({
  id: uuidSchema,
  /** `HD-000042`, per organisation. */
  ticketNo: z.string(),
  subject: z.string(),
  status: helpdeskTicketStatusSchema,
  priority: helpdeskPrioritySchema,
  categoryId: uuidSchema,
  categoryName: z.string(),
  isConfidential: z.boolean(),
  raiser: personRefSchema,
  assignee: personRefSchema.nullable(),
  slaClock: helpdeskSlaClockSchema,
  firstResponse: helpdeskSlaStateSchema,
  resolution: helpdeskSlaStateSchema,
  raisedAt: instantSchema,
  lastActivityAt: instantSchema,
});
export type HelpdeskTicketRow = z.infer<typeof helpdeskTicketRowSchema>;

export const helpdeskAttachmentSchema = z.object({
  id: uuidSchema,
  fileName: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  visibility: helpdeskVisibilitySchema,
  uploadedBy: personRefSchema,
  createdAt: instantSchema,
});

export const helpdeskCommentSchema = z.object({
  id: uuidSchema,
  visibility: helpdeskVisibilitySchema,
  author: personRefSchema,
  /** True when the author is the raiser — the thread renders the two sides differently. */
  byRaiser: z.boolean(),
  body: z.string(),
  createdAt: instantSchema,
});
export type HelpdeskComment = z.infer<typeof helpdeskCommentSchema>;

export const helpdeskEventSchema = z.object({
  id: uuidSchema,
  kind: helpdeskEventKindSchema,
  /** Null for the system (the SLA sweep, auto-close). */
  actor: personRefSchema.nullable(),
  fromValue: z.string().nullable(),
  toValue: z.string().nullable(),
  visibility: helpdeskVisibilitySchema,
  createdAt: instantSchema,
});

export const helpdeskTicketDetailSchema = helpdeskTicketRowSchema.extend({
  description: z.string(),
  resolvedAt: instantSchema.nullable(),
  closedAt: instantSchema.nullable(),
  rating: z.number().int().min(1).max(5).nullable(),
  ratingComment: z.string().nullable(),
  /** Internal entries are absent for the raiser. */
  comments: z.array(helpdeskCommentSchema),
  attachments: z.array(helpdeskAttachmentSchema),
  events: z.array(helpdeskEventSchema),
  /** What THIS caller may do to THIS ticket. Decided server-side; the screen renders it. */
  actions: z.object({
    canComment: z.boolean(),
    canAddInternalNote: z.boolean(),
    canAssign: z.boolean(),
    /** Statuses the caller may move it to from where it is now. */
    allowedStatuses: z.array(helpdeskTicketStatusSchema),
    canChangePriorityOrCategory: z.boolean(),
    canReopen: z.boolean(),
    canRate: z.boolean(),
  }),
});
export type HelpdeskTicketDetail = z.infer<typeof helpdeskTicketDetailSchema>;

export const listHelpdeskTicketsQuerySchema = z.object({
  /**
   * `mine` — raised by the caller; `assigned` — assigned to the caller; `queue` — every ticket of
   * the caller's teams' categories; `all` — every ticket (Helpdesk Managers). A scope the caller
   * cannot use is a 403, not a silently shorter list.
   */
  scope: helpdeskTicketScopeSchema.default('mine'),
  status: helpdeskTicketStatusSchema.optional(),
  /** `active` = open, in progress or waiting on the employee. */
  state: z.enum(['active', 'done']).optional(),
  categoryId: uuidSchema.optional(),
  priority: helpdeskPrioritySchema.optional(),
  assigneePersonId: uuidSchema.optional(),
  unassignedOnly: z.coerce.boolean().optional(),
  breachedOnly: z.coerce.boolean().optional(),
  search: z.string().trim().max(120).optional(),
  page: z.coerce.number().int().positive().default(1),
  pageSize: z.coerce.number().int().positive().max(100).default(25),
});

export const helpdeskTicketListSchema = z.object({
  items: z.array(helpdeskTicketRowSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
  scope: helpdeskTicketScopeSchema,
});

export const raiseHelpdeskTicketBodySchema = z.object({
  categoryId: uuidSchema,
  priority: helpdeskPrioritySchema.default('medium'),
  subject: z.string().trim().min(3).max(200),
  description: z.string().trim().min(3).max(10_000),
});

export const addHelpdeskCommentBodySchema = z.object({
  body: z.string().trim().min(1).max(10_000),
  /** `internal` is refused (403) for the raiser and anyone not working the ticket. */
  visibility: helpdeskVisibilitySchema.default('public'),
});

/** Base64 JSON like expense receipts. PDF, JPEG or PNG, at most 5 MB. */
export const uploadHelpdeskAttachmentBodySchema = z.object({
  fileName: z.string().trim().min(1).max(255),
  mimeType: z.string().trim().min(1).max(100),
  contentBase64: z.string().min(1),
  visibility: helpdeskVisibilitySchema.default('public'),
});

export const helpdeskAttachmentFileSchema = z.object({
  fileName: z.string(),
  mimeType: z.string(),
  contentBase64: z.string(),
});

export const assignHelpdeskTicketBodySchema = z.discriminatedUnion('mode', [
  /** Must be a member of the category's team (a Helpdesk Manager may name any manager too). */
  z.object({ mode: z.literal('person'), assigneePersonId: uuidSchema }),
  /** The next member in the team's rotation, skipping the raiser. */
  z.object({ mode: z.literal('round_robin') }),
  z.object({ mode: z.literal('unassign') }),
]);

export const changeHelpdeskStatusBodySchema = z.object({
  status: helpdeskTicketStatusSchema,
  /** Posted as a public comment with the change, e.g. the resolution. */
  comment: z.string().trim().max(10_000).optional(),
});

export const updateHelpdeskTicketBodySchema = z
  .object({
    priority: helpdeskPrioritySchema.optional(),
    categoryId: uuidSchema.optional(),
  })
  .refine((b) => b.priority !== undefined || b.categoryId !== undefined, {
    message: 'Change the priority, the category, or both',
  });

export const reopenHelpdeskTicketBodySchema = z.object({
  reason: z.string().trim().min(3).max(2000),
});

export const rateHelpdeskTicketBodySchema = z.object({
  rating: z.number().int().min(1).max(5),
  comment: z.string().trim().max(2000).optional(),
});

// ---------------------------------------------------------------------------
// Reports
// ---------------------------------------------------------------------------

export const helpdeskReportQuerySchema = z.object({
  /** Tickets raised in [from, to). Defaults to the last 30 days. */
  from: instantSchema.optional(),
  to: instantSchema.optional(),
  categoryId: uuidSchema.optional(),
});

const countRow = z.object({ key: z.string(), label: z.string(), count: z.number().int() });

export const helpdeskVolumeReportSchema = z.object({
  from: instantSchema,
  to: instantSchema,
  total: z.number().int().nonnegative(),
  byCategory: z.array(countRow),
  byStatus: z.array(countRow),
  byPriority: z.array(countRow),
});

const complianceSchema = z.object({
  met: z.number().int().nonnegative(),
  breached: z.number().int().nonnegative(),
  /** Not yet met and not yet due. Excluded from the rate. */
  pending: z.number().int().nonnegative(),
  /** met / (met + breached), 0–100 with one decimal, or null when nothing is decided. */
  compliancePercent: z.number().nullable(),
});

export const helpdeskSlaReportSchema = z.object({
  from: instantSchema,
  to: instantSchema,
  firstResponse: complianceSchema,
  resolution: complianceSchema,
  /** Elapsed calendar hours, one decimal — what the raiser waited. Null with no data. */
  averageFirstResponseHours: z.number().nullable(),
  averageResolutionHours: z.number().nullable(),
  byCategory: z.array(
    z.object({
      categoryId: uuidSchema,
      categoryName: z.string(),
      firstResponse: complianceSchema,
      resolution: complianceSchema,
    }),
  ),
});

export const helpdeskCsatReportSchema = z.object({
  from: instantSchema,
  to: instantSchema,
  ratedCount: z.number().int().nonnegative(),
  /** Resolved or closed tickets in the window — the denominator of the response rate. */
  resolvedCount: z.number().int().nonnegative(),
  averageRating: z.number().nullable(),
  /** Counts for ratings 1..5, in that order. */
  distribution: z.array(z.number().int().nonnegative()).length(5),
  byCategory: z.array(
    z.object({
      categoryId: uuidSchema,
      categoryName: z.string(),
      ratedCount: z.number().int().nonnegative(),
      averageRating: z.number().nullable(),
    }),
  ),
});

// ---------------------------------------------------------------------------
// Router
// ---------------------------------------------------------------------------

const TICKET_READERS = ['ticket:read', 'ticket:manage'] as const;
const CONFIG_READERS = ['ticket:manage', 'ticket.config:manage'] as const;

const read = { 401: errorBodySchema, 403: errorBodySchema } as const;
/** 422: a body that parses but names something unusable — an inactive category, a non-member. */
const write = {
  401: errorBodySchema,
  403: errorBodySchema,
  404: errorBodySchema,
  422: errorBodySchema,
} as const;

export const helpdeskContract = c.router(
  {
    getMe: {
      method: 'GET',
      path: '/me',
      responses: { 200: helpdeskMeSchema, 401: errorBodySchema },
      summary: 'What the caller can do in the helpdesk',
      metadata: routeMeta(null),
    },

    // ---- Tickets ---------------------------------------------------------------------------
    listTickets: {
      method: 'GET',
      path: '/tickets',
      query: listHelpdeskTicketsQuerySchema,
      responses: { 200: helpdeskTicketListSchema, ...read },
      summary: 'Tickets in the chosen scope',
      metadata: routeMeta([...TICKET_READERS], { ai: 'hidden' }),
    },
    getTicket: {
      method: 'GET',
      path: '/tickets/:ticketId',
      pathParams: ticketParams,
      responses: { 200: helpdeskTicketDetailSchema, ...write },
      summary: 'One ticket with its thread, attachments and history, as the caller may see it',
      metadata: routeMeta([...TICKET_READERS], { ai: 'hidden' }),
    },
    raiseTicket: {
      method: 'POST',
      path: '/tickets',
      body: raiseHelpdeskTicketBodySchema,
      responses: { 201: helpdeskTicketDetailSchema, ...write },
      summary: 'Raise a helpdesk ticket',
      metadata: routeMeta('ticket:create'),
    },
    addComment: {
      method: 'POST',
      path: '/tickets/:ticketId/comments',
      pathParams: ticketParams,
      body: addHelpdeskCommentBodySchema,
      responses: { 201: helpdeskCommentSchema, ...write, 409: errorBodySchema },
      summary: 'Reply on a ticket, or add an internal note',
      metadata: routeMeta([...TICKET_READERS]),
    },
    uploadAttachment: {
      method: 'POST',
      path: '/tickets/:ticketId/attachments',
      pathParams: ticketParams,
      body: uploadHelpdeskAttachmentBodySchema,
      responses: { 201: helpdeskAttachmentSchema, ...write, 409: errorBodySchema },
      summary: 'Attach a file to a ticket',
      // The body carries a whole file as base64, which a model cannot produce.
      metadata: routeMeta([...TICKET_READERS], { ai: 'hidden' }),
    },
    downloadAttachment: {
      method: 'GET',
      path: '/tickets/:ticketId/attachments/:attachmentId',
      pathParams: z.object({ ticketId: uuidSchema, attachmentId: uuidSchema }),
      responses: { 200: helpdeskAttachmentFileSchema, ...write },
      summary: 'Download a ticket attachment',
      metadata: routeMeta([...TICKET_READERS], { ai: 'hidden' }),
    },
    assignTicket: {
      method: 'POST',
      path: '/tickets/:ticketId/assign',
      pathParams: ticketParams,
      body: assignHelpdeskTicketBodySchema,
      responses: { 200: helpdeskTicketDetailSchema, ...write, 409: errorBodySchema },
      summary: 'Assign, reassign, round-robin or unassign a ticket',
      metadata: routeMeta('ticket:manage'),
    },
    changeStatus: {
      method: 'POST',
      path: '/tickets/:ticketId/status',
      pathParams: ticketParams,
      body: changeHelpdeskStatusBodySchema,
      responses: { 200: helpdeskTicketDetailSchema, ...write, 409: errorBodySchema },
      summary: 'Move a ticket through its lifecycle',
      metadata: routeMeta('ticket:manage'),
    },
    updateTicket: {
      method: 'PATCH',
      path: '/tickets/:ticketId',
      pathParams: ticketParams,
      body: updateHelpdeskTicketBodySchema,
      responses: { 200: helpdeskTicketDetailSchema, ...write, 409: errorBodySchema },
      summary: "Change a ticket's priority or category; its SLA due times are recomputed",
      metadata: routeMeta('ticket:manage'),
    },
    reopenTicket: {
      method: 'POST',
      path: '/tickets/:ticketId/reopen',
      pathParams: ticketParams,
      body: reopenHelpdeskTicketBodySchema,
      responses: { 200: helpdeskTicketDetailSchema, ...write, 409: errorBodySchema },
      summary: 'Reopen a resolved or closed ticket within the reopen window',
      metadata: routeMeta([...TICKET_READERS]),
    },
    rateTicket: {
      method: 'POST',
      path: '/tickets/:ticketId/rating',
      pathParams: ticketParams,
      body: rateHelpdeskTicketBodySchema,
      responses: { 200: helpdeskTicketDetailSchema, ...write, 409: errorBodySchema },
      summary: 'Rate a resolved ticket (1–5), which closes it',
      metadata: routeMeta('ticket:read'),
    },

    // ---- Configuration ---------------------------------------------------------------------
    listCategories: {
      method: 'GET',
      path: '/categories',
      query: z.object({ includeInactive: z.coerce.boolean().default(false) }),
      responses: { 200: z.array(helpdeskCategorySchema), ...read },
      summary: 'Ticket categories',
      metadata: routeMeta(['ticket:create', ...TICKET_READERS, 'ticket.config:manage']),
    },
    createCategory: {
      method: 'POST',
      path: '/categories',
      body: createHelpdeskCategoryBodySchema,
      responses: { 201: helpdeskCategorySchema, ...write, 409: errorBodySchema },
      summary: 'Add a ticket category',
      metadata: routeMeta('ticket.config:manage'),
    },
    updateCategory: {
      method: 'PATCH',
      path: '/categories/:categoryId',
      pathParams: z.object({ categoryId: uuidSchema }),
      body: updateHelpdeskCategoryBodySchema,
      responses: { 200: helpdeskCategorySchema, ...write, 409: errorBodySchema },
      summary: 'Rename, re-team, make confidential or deactivate a category',
      metadata: routeMeta('ticket.config:manage'),
    },
    listTeams: {
      method: 'GET',
      path: '/teams',
      responses: { 200: z.array(helpdeskTeamSchema), ...read },
      summary: 'Helpdesk teams with their members and categories',
      metadata: routeMeta([...CONFIG_READERS]),
    },
    createTeam: {
      method: 'POST',
      path: '/teams',
      body: createHelpdeskTeamBodySchema,
      responses: { 201: helpdeskTeamSchema, ...write, 409: errorBodySchema },
      summary: 'Create a helpdesk team',
      metadata: routeMeta('ticket.config:manage'),
    },
    updateTeam: {
      method: 'PATCH',
      path: '/teams/:teamId',
      pathParams: z.object({ teamId: uuidSchema }),
      body: updateHelpdeskTeamBodySchema,
      responses: { 200: helpdeskTeamSchema, ...write, 409: errorBodySchema },
      summary: "Change a team's members, lead, assignment mode or name",
      metadata: routeMeta('ticket.config:manage'),
    },
    getSlaTargets: {
      method: 'GET',
      path: '/sla-targets',
      responses: { 200: z.array(helpdeskSlaTargetSchema), ...read },
      summary: 'First-response and resolution targets per category and priority',
      metadata: routeMeta([...CONFIG_READERS]),
    },
    putSlaTargets: {
      method: 'PUT',
      path: '/sla-targets',
      body: putHelpdeskSlaTargetsBodySchema,
      responses: { 200: z.array(helpdeskSlaTargetSchema), ...write },
      summary: 'Replace the SLA matrix; applies to tickets raised from now on',
      metadata: routeMeta('ticket.config:manage'),
    },
    getSettings: {
      method: 'GET',
      path: '/settings',
      responses: { 200: helpdeskSettingsSchema, ...read },
      summary: 'SLA clock, working day and reopen window',
      metadata: routeMeta([...CONFIG_READERS]),
    },
    putSettings: {
      method: 'PUT',
      path: '/settings',
      body: helpdeskSettingsSchema,
      responses: { 200: helpdeskSettingsSchema, ...write },
      summary: 'Set the SLA clock, working day and reopen window',
      metadata: routeMeta('ticket.config:manage'),
    },

    // ---- Reports ---------------------------------------------------------------------------
    getVolumeReport: {
      method: 'GET',
      path: '/reports/volume',
      query: helpdeskReportQuerySchema,
      responses: { 200: helpdeskVolumeReportSchema, ...read },
      summary: 'Tickets raised by category, status and priority',
      metadata: routeMeta('ticket.report:read'),
    },
    getSlaReport: {
      method: 'GET',
      path: '/reports/sla',
      query: helpdeskReportQuerySchema,
      responses: { 200: helpdeskSlaReportSchema, ...read },
      summary: 'SLA compliance and average first-response and resolution times',
      metadata: routeMeta('ticket.report:read'),
    },
    getCsatReport: {
      method: 'GET',
      path: '/reports/csat',
      query: helpdeskReportQuerySchema,
      responses: { 200: helpdeskCsatReportSchema, ...read },
      summary: 'Satisfaction ratings of resolved tickets',
      metadata: routeMeta('ticket.report:read'),
    },
  },
  { pathPrefix: '/helpdesk' },
);
